import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { RISK_META, RISK_FALLBACK, formatLastSeen } from "@/lib/risk";
import { RiskBadge, PocChip } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import ContactsPanel from "@/components/ContactsPanel";
import ClassificationPills from "@/components/ClassificationBreakdown";
import {
  ArrowLeft, MapPin, Globe, PhoneCall, UsersThree, Crown, Note,
} from "@phosphor-icons/react";
import NotesDialog from "@/components/NotesDialog";
import FirmCallLogsDialog from "@/components/FirmCallLogsDialog";
import { CloudPill } from "@/pages/Enterprise";

// Period keys must match Grafana panel titles
const RANGES = [
  { key: "1D", label: "Today" },
  { key: "2D", label: "Yesterday" },
  { key: "7D", label: "7D" },
  { key: "30D", label: "30D" },
  { key: "90D", label: "90D" },
  { key: "180D", label: "180D" },
  { key: "365D", label: "365D" },
  { key: "ITD", label: "ITD" },
];

// Mixpanel period key (days) for each range
const MX_PERIOD = {
  "1D": "1",
  "2D": "1",
  "7D": "7",
  "30D": "30",
  "90D": "90",
  "180D": "180",
  "365D": "365",
  "ITD": "itd",
};

const EVENTS = [
  "Total Events",
  "Import Books",
  "Ledger Classification",
  "Statement Generation",
  "Ledger Scrutiny",
  "Issues Raised",
  "Doc Request Raised",
  "Workflow Actions",
];

const GRAFANA_FIELDS = [
  { key: "new_clients", label: "New clients", itdLabel: "Total clients" },
  { key: "new_assignments", label: "New assignments", itdLabel: "Total assignments" },
  { key: "closed_assignments", label: "Closed assignments", itdLabel: "Closed assignments" },
  { key: "new_issues", label: "New issues", itdLabel: "Total issues" },
  { key: "new_doc_requests", label: "New doc requests", itdLabel: "Total doc requests" },
  { key: "new_ledgers", label: "New ledgers", itdLabel: "Total ledgers" },
  { key: "new_vouchers", label: "New vouchers", itdLabel: "Total vouchers" },
  { key: "new_manually_created_letter_templates", label: "New letter templates", itdLabel: "Total letter templates" },
  { key: "new_checklists_filled", label: "New checklists filled", itdLabel: "Total checklists filled" },
  { key: "new_completed_materiality_tasks", label: "New materiality tasks", itdLabel: "Total materiality tasks" },
  { key: "new_completed_fsli_testing_tasks", label: "New FSLI testing tasks", itdLabel: "Total FSLI testing tasks" },
  { key: "new_generated_workpapers", label: "New workpapers", itdLabel: "Total workpapers" },
];

export default function Firm360() {
  const { subdomain } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [firm, setFirm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("30D");
  const [showLogs, setShowLogs] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [notesCount, setNotesCount] = useState(0);
  const [callLogsCount, setCallLogsCount] = useState(0);
  // Bump to force the FirmCallLogsDialog to refetch after a new log is saved.
  const [callLogVersion, setCallLogVersion] = useState(0);

  useEffect(() => {
    setLoading(true);
    api.get(`/firms/${subdomain}`).then((r) => {
      setFirm(r.data);
      setNotesCount(r.data.notes_count ?? 0);
      setCallLogsCount(r.data.call_logs_count ?? 0);
    }).finally(() => setLoading(false));
  }, [subdomain]);

  const back = () => {
    // `from` may now carry the live URL the user came from (pathname + search),
    // URL-encoded so filters survive. Legacy short form (just a pathname) still
    // works because navigate() accepts either.
    const from = searchParams.get("from");
    const legacyReturn = searchParams.get("return") || "";
    if (from) {
      const target = decodeURIComponent(from);
      navigate(legacyReturn && !target.includes("?") ? `${target}?${legacyReturn}` : target);
      return;
    }
    navigate("/dashboard");
  };

  // Strip the query string before looking up the label — older callers pass
  // a bare pathname (e.g. "/dashboard"), newer callers pass the full URL.
  const fromParam = searchParams.get("from") || "/dashboard";
  const fromPath = decodeURIComponent(fromParam).split("?")[0];
  const BACK_LABELS = {
    "/dashboard": "Back to Dashboard",
    "/new-customers": "Back to New Customers",
    "/old-customers": "Back to Old Customers",
    "/enterprise": "Back to Enterprise",
    "/renewals": "Back to Renewals",
    "/power-users": "Back to Power Users",
    "/usage-metrics": "Back to Usage Metrics",
    "/activity": "Back to Activity",
    "/admin": "Back to Admin",
  };
  const backLabel = BACK_LABELS[fromPath] || "Back";

  const meta = firm ? RISK_META[firm.risk] || RISK_FALLBACK : null;
  const cumulative = firm?.grafana_cumulative || {};

  const mxRows = useMemo(() => {
    if (!firm) return [];
    
    const counts = firm.event_counts || {};
    const lastSeenMap = firm.event_last_seen || {};
    
    // For ITD, read from event_counts["itd"] (populated by ETL)
    if (range === "ITD") {
      const itdCounts = counts["itd"] || {};
      return EVENTS.map((ev) => ({
        event: ev,
        count: itdCounts[ev] || 0,
        last_seen: lastSeenMap[ev], // Use same last_seen as other periods
      }));
    }
    
    // For other periods
    const periodFor = (r) => (r === "1D" ? "1" : r === "2D" ? "1" : r.replace("D", ""));
    const mxPeriod = periodFor(range);
    return EVENTS.map((ev) => ({
      event: ev,
      count: range === "2D"
        ? Math.max(0, Math.round(((counts["7"]?.[ev] || 0) - (counts["1"]?.[ev] || 0)) / 6))
        : counts[mxPeriod]?.[ev] || 0,
      last_seen: lastSeenMap[ev],
    }));
  }, [firm, range]);

  const gfRow = useMemo(() => {
    if (!firm) return {};
    if (range === "ITD") {
      // For ITD, use grafana_cumulative all-time totals
      const cumulative = firm.grafana_cumulative || {};
      return {
        new_clients: cumulative.total_clients || 0,
        new_assignments: cumulative.total_assignments || 0,
        closed_assignments: cumulative.total_closed_assignments || 0,
        new_issues: cumulative.total_issues_raised || 0,
        new_doc_requests: cumulative.total_doc_request_raised || 0,
        new_ledgers: cumulative.total_ledgers || 0,
        new_vouchers: cumulative.total_vouchers || 0,
        new_manually_created_letter_templates: cumulative.total_manually_created_letter_templates || 0,
        new_checklists_filled: cumulative.total_checklists_filled || 0,
        new_completed_materiality_tasks: cumulative.total_completed_materiality_tasks || 0,
        new_completed_fsli_testing_tasks: cumulative.total_completed_fsli_testing_tasks || 0,
        new_generated_workpapers: cumulative.total_generated_workpapers || 0,
      };
    }
    const periods = firm.grafana_periods || {};
    return periods[range] || {};
  }, [firm, range]);

  if (loading) {
    return <div className="text-sm text-gray-500 py-12 text-center">Loading firm…</div>;
  }
  if (!firm) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
        <div className="text-sm text-gray-500 mb-4">Firm not found.</div>
        <Button onClick={back} variant="outline" data-testid="firm360-back-empty">
          {backLabel}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <button
        onClick={back}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
        data-testid="firm360-back-btn"
      >
        <ArrowLeft size={16} />
        {backLabel}
      </button>

      {firm.status === "Churned" && (
        <div
          className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3"
          data-testid="firm360-churn-banner"
        >
          <div className="w-1 self-stretch bg-red-500 rounded-full" />
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[11px] uppercase tracking-wider text-red-700 font-semibold">
                Churned
              </span>
              {firm.churn_date && (
                <span className="text-[11px] text-red-600 font-mono">· {firm.churn_date}</span>
              )}
              {firm.churn_reason && (
                <span className="inline-flex items-center bg-red-100 text-red-700 text-[10px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded-full border border-red-200">
                  {firm.churn_reason}
                </span>
              )}
            </div>
            {firm.churn_notes && (
              <div className="text-sm text-red-800 leading-relaxed">{firm.churn_notes}</div>
            )}
            {firm.churned_by && (
              <div className="text-[11px] text-red-500/80 mt-1 font-mono">
                Marked by {firm.churned_by}
                {firm.churned_at ? ` · ${new Date(firm.churned_at).toLocaleDateString()}` : ""}
              </div>
            )}
          </div>
        </div>
      )}

      <div
        className={`bg-white border border-gray-200 rounded-lg p-6 ${meta.leftClass}`}
        data-testid="firm360-header"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h1 className="font-display text-3xl font-semibold tracking-tight text-gray-900 break-words">
              {firm.firm_name}
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-gray-500">
              <a
                href={`https://${firm.firm_url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 font-mono hover:text-gray-900 transition-colors"
              >
                <Globe size={14} />
                {firm.firm_url}
              </a>
              {firm.aliases && firm.aliases.length > 0 && (
                <span
                  className="inline-flex items-center gap-1 text-[11px] font-mono text-violet-700 bg-violet-50 border border-violet-200 rounded-full px-2 py-0.5"
                  title={firm.aliases.join("\n")}
                  data-testid="firm-aliases-badge"
                >
                  +{firm.aliases.length} alias{firm.aliases.length === 1 ? "" : "es"}
                </span>
              )}
              <span className="text-gray-300">·</span>
              <span className="flex items-center gap-1" data-testid="firm360-users">
                <UsersThree size={14} />
                {firm.num_users && firm.num_users !== "-"
                  ? String(firm.num_users).toLowerCase() === "unlimited"
                    ? "Unlimited users"
                    : `${firm.num_users} users`
                  : "— users"}
              </span>
              <span className="text-gray-300">·</span>
              <span className="flex items-center gap-1" data-testid="firm360-tier">
                <Crown size={14} />
                {firm.tier}
              </span>
              {firm.tier === "Enterprise" && firm.cloud_plan && (
                <>
                  <span className="text-gray-300">·</span>
                  <span data-testid="firm360-cloud">
                    <CloudPill plan={firm.cloud_plan} />
                  </span>
                </>
              )}
              <span className="text-gray-300">·</span>
              <span className="flex items-center gap-1">
                <MapPin size={14} />
                {firm.city}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            {firm.status !== "Churned" && <ClassificationPills firm={firm} />}
            <div className="inline-flex items-center gap-1" data-testid="firm360-pocs">
              <PocChip name={firm.poc} />
              {firm.sales_poc ? <PocChip name={firm.sales_poc} variant="sales" /> : null}
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowLogs(true)}
              data-testid="firm360-call-logs-btn"
              className="ml-1"
            >
              <PhoneCall size={14} className="mr-1.5" />
              Call Logs
              {callLogsCount > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-blue-100 text-blue-700 text-[10px] font-semibold" data-testid="firm360-call-logs-count">
                  {callLogsCount}
                </span>
              )}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowNotes(true)}
              data-testid="firm360-notes-btn"
            >
              <Note size={14} weight="fill" className="mr-1.5 text-violet-600" />
              Notes
              {notesCount > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-violet-100 text-violet-700 text-[10px] font-semibold" data-testid="firm360-notes-count">
                  {notesCount}
                </span>
              )}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-4 mt-6 pt-6 border-t border-gray-100">
          <Stat label="Last seen" value={formatLastSeen(firm.last_seen, firm.days_since_seen)} />
          <Stat label="Activation" value={firm.activation_date} mono />
          <Stat label="Renewal" value={firm.subscription_end_date || "—"} mono />
          <Stat label="Total clients" value={(cumulative.total_clients ?? firm.total_clients).toLocaleString()} mono />
          <Stat label="Total assignments" value={(cumulative.total_assignments ?? firm.total_assignments).toLocaleString()} mono />
          <Stat label="Open assignments" value={(cumulative.total_open_assignments ?? 0).toLocaleString()} mono />
          <Stat label="Closed assignments" value={(cumulative.total_closed_assignments ?? 0).toLocaleString()} mono />
          <Stat label="Total ledgers" value={(cumulative.total_ledgers ?? 0).toLocaleString()} mono />
          <Stat label="Total vouchers" value={(cumulative.total_vouchers ?? 0).toLocaleString()} mono />
        </div>
      </div>

      {/* Contacts directory */}
      <ContactsPanel firmSubdomain={firm.subdomain} />

      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-semibold text-gray-900">Activity</h2>
        <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg p-1" data-testid="range-selector">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                range === r.key
                  ? "bg-gray-900 text-white"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              }`}
              data-testid={`range-${r.key}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Mixpanel events */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="event-activity-table">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between">
            <span className="font-display text-sm font-semibold text-gray-700 uppercase tracking-wider">
              Mixpanel events
            </span>
            <span className="text-[11px] text-gray-500">
              {RANGES.find((r) => r.key === range)?.label}
            </span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr className="text-left">
                <th className="px-4 py-2 text-[11px] uppercase tracking-wider font-medium text-gray-500">
                  Event
                </th>
                <th className="px-4 py-2 text-[11px] uppercase tracking-wider font-medium text-gray-500">
                  Last used
                </th>
                <th className="px-4 py-2 text-[11px] uppercase tracking-wider font-medium text-gray-500 text-right">
                  Count
                </th>
              </tr>
            </thead>
            <tbody>
              {mxRows.map((row) => (
                <tr key={row.event} className="border-b border-gray-100 last:border-b-0">
                  <td className="px-4 py-2.5 font-medium text-gray-800">{row.event}</td>
                  <td className="px-4 py-2.5 text-gray-600 font-mono text-xs">
                    {row.last_seen || "—"}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono tabular-nums text-gray-900">
                    {row.count.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {range === "ITD" && (
            <div className="px-4 py-2 text-[11px] text-gray-500 bg-gray-50 border-t border-gray-100">
              Data shown from activation date. May be limited by Mixpanel plan retention period.
            </div>
          )}
        </div>

        {/* Grafana metrics */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden relative" data-testid="grafana-activity-table">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between">
            <span className="font-display text-sm font-semibold text-gray-700 uppercase tracking-wider">
              Grafana usage delta
            </span>
            <span className="text-[11px] text-gray-500">
              {RANGES.find((r) => r.key === range)?.label}
            </span>
          </div>
          {Object.keys(gfRow).length === 0 ? (
            <div className="px-4 py-4 text-center text-xs text-gray-400 italic">
              No Grafana data yet — run ETL from Admin → Data Sync.
            </div>
          ) : (
            <>
              <div className="h-[320px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-100 sticky top-0">
                    <tr className="text-left">
                      <th className="px-4 py-2 text-[11px] uppercase tracking-wider font-medium text-gray-500">
                        Metric
                      </th>
                      <th className="px-4 py-2 text-[11px] uppercase tracking-wider font-medium text-gray-500 text-right">
                        {range === "ITD" ? "Total" : "Change"}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {GRAFANA_FIELDS.map((f) => (
                      <tr key={f.key} className="border-b border-gray-100 last:border-b-0">
                        <td className="px-4 py-2.5 font-medium text-gray-800">
                          {range === "ITD" ? f.itdLabel : f.label}
                        </td>
                        <td className="px-4 py-2.5 text-right font-mono tabular-nums text-gray-900">
                          {(gfRow[f.key] ?? 0).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Scroll indicator gradient */}
              <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white to-transparent pointer-events-none" />
            </>
          )}
        </div>
      </div>

      {/* Call Logs modal */}
      <FirmCallLogsDialog
        open={showLogs}
        onClose={() => setShowLogs(false)}
        firm={firm}
        refreshKey={callLogVersion}
        onCountChange={setCallLogsCount}
      />

      {/* Notes dialog — add/edit/delete free-form notes per firm */}
      <NotesDialog
        open={showNotes}
        onOpenChange={(v) => { if (!v) setShowNotes(false); }}
        firm={firm}
        onCountChange={setNotesCount}
      />
    </div>
  );
}

function Stat({ label, value, mono }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wide text-gray-500 font-medium whitespace-nowrap">{label}</div>
      <div className={`mt-1 text-base font-semibold text-gray-900 ${mono ? "font-mono tabular-nums text-sm" : ""}`}>
        {value}
      </div>
    </div>
  );
}
